import axios from "axios";
import { message } from "components/Messages/Messages";
import { CompanyUser, User } from "interfaces/users";
import React, { useCallback, useContext, useEffect, useState } from "react";
import { ActiveMember } from "../../interfaces/activeMember";
import { REFRESH_TOKEN_KEY, TOKEN_KEY } from "./constants";
import { parseJwt } from "./helpers";
import { ApiTokenPayload } from "./interfaces";

interface LoginContextValue {
  currentUser: User | undefined;
  isLoggedIn: boolean;
  isLoadingCurrentUser: boolean;
  logIn: (credentials: { username: string; password: string }) => Promise<void>;
  logOut: () => void;
}

const LoginContext = React.createContext<LoginContextValue | null>(null);

const LoginContextProvider: React.FC<React.PropsWithChildren<Record<string, unknown>>> = ({ children }) => {
  const [currentUser, setCurrentUser] = useState<User>();
  const [isLoadingCurrentUser, setIsLoadingCurrentUser] = useState(true);

  const logOut = useCallback((showMessage = true) => {
    localStorage.removeItem(TOKEN_KEY);
    localStorage.removeItem(REFRESH_TOKEN_KEY);
    setCurrentUser(undefined);
    delete axios.defaults.headers.common.Authorization;
    if (showMessage) message("info", "Du er logget ut");
  }, []);

  const logIn = useCallback(
    (credentials: { username: string; password: string }) => {
      setIsLoadingCurrentUser(true);
      // Ensure empty login state before logging in
      logOut(false);
      return axios
        .post<{ refresh: string; access: string; user: User }>("/api/get-token/", credentials)
        .then(response => {
          const token = response.data.access;
          localStorage.setItem(TOKEN_KEY, token);
          localStorage.setItem(REFRESH_TOKEN_KEY, response.data.refresh);
          axios.defaults.headers.common.Authorization = `Bearer ${token}`;
          setCurrentUser(response.data.user);
        })
        .finally(() => setIsLoadingCurrentUser(false));
    },
    [logOut]
  );

  useEffect(function getUserInfoIfAlreadyLoggedIn() {
    const token = localStorage.getItem(TOKEN_KEY);
    if (token) {
      const payload = parseJwt<ApiTokenPayload>(token);
      axios.defaults.headers.common.Authorization = `Bearer ${token}`;
      axios
        .get<User>(`/users/${payload.user_id}/`)
        .then(response => setCurrentUser(response.data))
        .finally(() => setIsLoadingCurrentUser(false));
    } else {
      setIsLoadingCurrentUser(false);
    }
  }, []);

  return (
    <LoginContext.Provider
      value={{ currentUser, isLoggedIn: currentUser !== undefined, isLoadingCurrentUser, logIn, logOut }}
    >
      {children}
    </LoginContext.Provider>
  );
};

export const useLoginContext = () =>
  useContext(LoginContext) ?? {
    currentUser: undefined,
    isLoggedIn: false,
    isLoadingCurrentUser: false,
    logIn: () => {
      throw Error("Could not find LoginContext");
    },
    logOut: () => {
      throw Error("Could not find LoginContext");
    },
  };

export const isCompanyUser = (user?: User): user is CompanyUser => (user ? "name" in user : false);
export const isBlMemberUser = (user?: User): user is ActiveMember =>
  user ? "is_active_member" in user && user.is_active_member : false;

export default LoginContextProvider;
